import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import CustomButton from "../../components/Helpers/CustomButton";

export default function WifiDisconnectionReport() {
    const { properties } = useSelector((state) => state.properties);
    const [loading, setLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [propertyId, setPropertyId] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [disconnectedTenants, setDisconnectedTenants] = useState([]);
    const [summary, setSummary] = useState(null);

    useEffect(() => {
        fetchData();
        fetchSummary();
    }, []);

    const fetchData = async (propId = "", search = "") => {
        setLoading(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}wifi-disconnection-report`, {
                params: {
                    property_id: propId || propertyId,
                    search: search || searchTerm,
                },
            });

            if (response.data.success) {
                setDisconnectedTenants(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching WiFi disconnection data:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchSummary = async (propId = "") => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}wifi-disconnection-report/summary`, {
                params: {
                    property_id: propId || propertyId,
                },
            });

            if (response.data.success) {
                setSummary(response.data.summary);
            }
        } catch (error) {
            console.error("Error fetching summary:", error);
        }
    };

    const handlePropertyChange = (e) => {
        const propId = e.target.value;
        setPropertyId(propId);
        fetchData(propId, searchTerm);
        fetchSummary(propId);
    };

    const handleSearch = (e) => {
        const search = e.target.value;
        setSearchTerm(search);
        fetchData(propertyId, search);
    };

    const handleExport = async () => {
        setExportLoading(true);
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}wifi-disconnection-report/export`,
                { property_id: propertyId },
                { responseType: "blob" }
            );

            const href = URL.createObjectURL(response.data);
            const link = document.createElement("a");
            link.href = href;
            link.setAttribute("download", `wifi_disconnections_${new Date().toISOString().split("T")[0]}.xlsx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(href);
        } catch (error) {
            console.error("Error exporting report:", error);
            alert("Failed to export report");
        } finally {
            setExportLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return date.toLocaleString("en-ZA", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    return (
        <div className="h-full w-auto bg-gray-100 my-6 xl:my-0 xl:mx-4 rounded-3xl flex-1 p-8">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold text-gray-900">WiFi Disconnection Report</h1>
                <CustomButton
                    className="inline-flex items-center px-6 py-2.5 rounded-lg border text-sm font-medium focus:z-10 focus:outline-none border-primary bg-primary text-white hover:bg-primary_hover focus:ring-primary focus:ring-2 focus:ring-offset-2 transition duration-150 ease-in-out shadow-sm"
                    text="Export to Excel"
                    onClick={handleExport}
                    loading={exportLoading}
                />
            </div>

            {/* Summary Cards */}
            {summary && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div className="bg-white rounded-xl shadow-sm p-6">
                        <h3 className="text-sm font-medium text-gray-500 mb-2">Total Disconnected</h3>
                        <p className="text-3xl font-bold text-red-600">{summary.total_disconnected}</p>
                    </div>
                    <div className="bg-white rounded-xl shadow-sm p-6">
                        <h3 className="text-sm font-medium text-gray-500 mb-2">Total Connected</h3>
                        <p className="text-3xl font-bold text-green-600">{summary.total_connected}</p>
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Property</label>
                        <select
                            value={propertyId}
                            onChange={handlePropertyChange}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                        >
                            <option value="">All Properties</option>
                            {properties &&
                                properties.map((p) => (
                                    <option key={p.value} value={p.value}>
                                        {p.label}
                                    </option>
                                ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={handleSearch}
                            placeholder="Search by name, email, phone, or username..."
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                        />
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                {loading ? (
                    <div className="flex justify-center items-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                    </div>
                ) : disconnectedTenants.length === 0 ? (
                    <div className="text-center py-12">
                        <p className="text-gray-500 text-lg">No disconnected WiFi accounts found</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Booking Ref</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tenant Name</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Property</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {process.env.REACT_APP_COLOUR === "QL" ? "Unit" : "Unit/Bed"}
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">WiFi Username</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Disconnected At</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {disconnectedTenants.map((tenant) => (
                                    <tr key={tenant.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{tenant.booking_reference}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{tenant.tenant_name}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{tenant.property_name}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {process.env.REACT_APP_COLOUR === "QL" ? tenant.unit_name : `${tenant.unit_name} / ${tenant.bed_name}`}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{tenant.location}</td>
                                        <td className="px-6 py-4 text-sm text-gray-500">
                                            <div>{tenant.mobile_number}</div>
                                            <div className="text-xs text-gray-400">{tenant.email}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{tenant.username || "N/A"}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(tenant.disconnected_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Results Count */}
            {!loading && disconnectedTenants.length > 0 && (
                <div className="mt-4 text-sm text-gray-600">
                    Showing {disconnectedTenants.length} disconnected WiFi {disconnectedTenants.length === 1 ? "account" : "accounts"}
                </div>
            )}
        </div>
    );
}
